import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import { renderToString } from "react-dom/server";
import { useEffect } from "react";
import { InstantSearchSSRProvider, getServerState } from "react-instantsearch";
import { Provider } from "react-redux";
import Cookies from "js-cookie";
import { wrapper } from "@lib/store";
import { useCurrentUser, useProductsRented } from "@hooks/use-current-user";
import useContentLoaderAnimation from "@hooks/use-content-loader-animation";
import {
  useCatalogNavigation,
  useClearCatalogLastScrollY,
} from "@hooks/use-catalog-navigation-hooks";
import CanonicalUrl from "@components/layout/CanonicalUrl";
import ListingHead from "@components/views/Listing/ListingHead";
import Catalog from "@components/views/Catalog";
import { getHub } from "@utils/call-api/hubs-call";
import isServer from "@utils/is-server";
import CatalogHighlights from "@components/views/Catalog/CatalogHighlights";
import CustomLoader from "@components/core/CustomLoader";

const EmptyComponent = ({ children }) => <>{children}</>;

const Hub = ({ hub, context, page, url, typeOfGridCookie, withUser, serverState }) => {
  const user = useCurrentUser();
  const productsRented = useProductsRented();
  const catalogNavigation = useCatalogNavigation();
  const clearCatalogLastScrollY = useClearCatalogLastScrollY();
  const isFirstPage = page === "1" || !page;

  const Container = withUser ? EmptyComponent : InstantSearchSSRProvider;

  function canDisplayCatalog() {
    if (!isServer() && !user && Cookies.get("jwt-token")) return false;
    if (!isServer() && !productsRented && Cookies.get("jwt-token")) return false;

    return true;
  }

  const loaderShowDuration = 750;
  const { contentProps, loaderProps } = useContentLoaderAnimation(loaderShowDuration);

  useEffect(() => {
    setTimeout(() => {
      if (isServer()) return;

      window.scrollTo({ top: catalogNavigation?.lastScrollY, behavior: "instant" });
      clearCatalogLastScrollY();
    }, loaderShowDuration);
  }, []);

  return (
    <Container {...serverState}>
      <CanonicalUrl />
      <ListingHead
        page={page}
        isFirstPage={isFirstPage}
        seoTitle={hub.seo_title}
        seoDescription={hub.seo_description}
      />
      <div {...loaderProps}>
        <CustomLoader
          variant="Primary"
          style={{ width: "140px" }}
        />
      </div>
      <div {...contentProps}>
        <CatalogHighlights hub={hub} />
        {canDisplayCatalog() && (
          <Catalog
            context={context}
            hub={hub}
            seoContent={isFirstPage ? hub.seo_content : ""}
            url={url}
            typeOfGridCookie={typeOfGridCookie}
          />
        )}
      </div>
    </Container>
  );
};

export const getServerSideProps = wrapper.getServerSideProps(
  (store) =>
    async ({ req, query, locale }) => {
      let hub;

      try {
        hub = await getHub(query.hub, locale);
      } catch (error) {
        return {
          notFound: true,
        };
      }

      const { context } = hub.data;

      const protocol = req.headers.referer?.split("://")[0] || "https";
      const url = `${protocol}://${req.headers.host}${req.url}`;

      const jwt = req.cookies["jwt-token"];
      const typeOfGridCookie = req.cookies["type_of_catalog_grid"] || "small-pictures";

      const props = {
        hub: hub.data,
        context,
        page: query.page || "",
        url,
        typeOfGridCookie,
        headerBehaviors: {
          context,
          noHead: true,
        },
      };

      if (jwt) {
        return {
          props: {
            ...props,
            withUser: true,
            ...(await serverSideTranslations(locale, ["common", "catalog", "feedbacks"])),
          },
        };
      }

      const serverState = await getServerState(
        <Provider store={store}>
          <Hub
            hub={hub.data}
            context={context}
            page={query.page || ""}
            url={url}
            typeOfGridCookie={typeOfGridCookie}
            withUser={false}
          />
        </Provider>,
        {
          renderToString,
        },
      );

      return {
        props: {
          ...props,
          withUser: false,
          serverState,
          ...(await serverSideTranslations(locale, ["common", "catalog", "feedbacks"])),
        },
      };
    },
);

Hub.requireAuth = false;

export default Hub;
